import { useEffect, useState } from "react";
import API from "@src/API.js";
import BreadCrumb from "../Controls/BreadCrumb";
import CustomButton from "../Controls/CustomButton";
import { toast } from "react-toastify";

const REGIONAL_TYPES = [
  { value: 1, label: "Meetup / user group" },
  { value: 2, label: "Training" },
  { value: 3, label: "Job fair" },
  { value: 4, label: "Networking" },
];

const GLOBAL_TYPES = [
  { value: 10, label: "Conference" },
  { value: 11, label: "Symposium" },
  { value: 12, label: "Trade show" },
];

function eventTypeLabel(scope, eventType) {
  const list = scope === 1 ? REGIONAL_TYPES : GLOBAL_TYPES;
  return list.find((t) => t.value === eventType)?.label || `Type ${eventType}`;
}

function emptyForm(scope) {
  return {
    Scope: scope,
    EventType: scope === 1 ? 1 : 10,
    Title: "",
    Description: "",
    StartUtc: "",
    EndUtc: "",
    LocationName: "",
    CityName: "",
    RegionOrState: "",
    CountryName: "",
    Url: "",
  };
}

export default function SkillCalendar() {
  const [scope, setScope] = useState(1);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(() => emptyForm(1));
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  async function load(scopeFilter) {
    const resp = await API.getAsync(`/SkillCalendar?scope=${scopeFilter}`);
    if (resp?.ok) setEvents(resp.jsonData || []);
    else toast.error(resp?.jsonData?.message || "Could not load calendar");
  }

  useEffect(() => {
    load(scope);
  }, [scope]);

  function switchScope(next) {
    setScope(next);
    setForm(emptyForm(next));
    setShowForm(false);
  }

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onCreate(e) {
    e.preventDefault();
    if (busy) return;
    if (!form.Title.trim() || !form.StartUtc) {
      toast.error("Title and start date are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        Scope: scope,
        EventType: Number(form.EventType),
        Title: form.Title.trim(),
        Description: form.Description || null,
        StartUtc: new Date(form.StartUtc).toISOString(),
        EndUtc: form.EndUtc ? new Date(form.EndUtc).toISOString() : null,
        LocationName: form.LocationName || null,
        CityName: form.CityName || null,
        RegionOrState: form.RegionOrState || null,
        CountryName: form.CountryName || null,
        Url: form.Url || null,
      };
      const resp = await API.postAsync("/SkillCalendar", payload);
      if (!resp?.ok) {
        toast.error(resp?.jsonData?.message || "Save failed");
        return;
      }
      toast.success("Event added");
      setForm(emptyForm(scope));
      setShowForm(false);
      await load(scope);
    } finally {
      setBusy(false);
    }
  }

  const typeOptions = scope === 1 ? REGIONAL_TYPES : GLOBAL_TYPES;

  return (
    <div className="skillCalendar" data-testid="skill-calendar">
      <BreadCrumb props={["Skill Calendar"]} />
      <h1>Skill Calendar</h1>
      <p>Regional meetups and training, plus global conferences and trade shows.</p>

      <div className="calendarTabs" role="tablist">
        <CustomButton
          type="button"
          variant="outlined"
          color="secondary"
          role="tab"
          aria-selected={scope === 1}
          className={scope === 1 ? "active" : undefined}
          data-testid="tab-regional"
          onClick={() => switchScope(1)}
        >
          Regional
        </CustomButton>
        <CustomButton
          type="button"
          variant="outlined"
          color="secondary"
          role="tab"
          aria-selected={scope === 2}
          className={scope === 2 ? "active" : undefined}
          data-testid="tab-global"
          onClick={() => switchScope(2)}
        >
          Global
        </CustomButton>
      </div>

      <div className="calendarActions">
        <CustomButton
          type="button"
          variant={showForm ? "outlined" : "contained"}
          color={showForm ? "secondary" : "primary"}
          data-testid="add-event"
          onClick={() => setShowForm((v) => !v)}
        >
          {showForm ? "Cancel" : "Add event"}
        </CustomButton>
      </div>

      {showForm ? (
        <form className="calendarForm" data-testid="calendar-form" onSubmit={onCreate}>
          <label>
            Type
            <select
              value={form.EventType}
              onChange={(ev) => updateField("EventType", Number(ev.target.value))}
              data-testid="event-type"
            >
              {typeOptions.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Title
            <input
              value={form.Title}
              onChange={(ev) => updateField("Title", ev.target.value)}
              data-testid="event-title"
              required
            />
          </label>
          <label>
            Start
            <input
              type="datetime-local"
              value={form.StartUtc}
              onChange={(ev) => updateField("StartUtc", ev.target.value)}
              data-testid="event-start"
              required
            />
          </label>
          <label>
            End (optional)
            <input
              type="datetime-local"
              value={form.EndUtc}
              onChange={(ev) => updateField("EndUtc", ev.target.value)}
            />
          </label>
          <label>
            Location
            <input value={form.LocationName} onChange={(ev) => updateField("LocationName", ev.target.value)} />
          </label>
          <label>
            City
            <input value={form.CityName} onChange={(ev) => updateField("CityName", ev.target.value)} />
          </label>
          <label>
            Region / State
            <input value={form.RegionOrState} onChange={(ev) => updateField("RegionOrState", ev.target.value)} />
          </label>
          <label>
            Country
            <input value={form.CountryName} onChange={(ev) => updateField("CountryName", ev.target.value)} />
          </label>
          <label>
            URL
            <input value={form.Url} onChange={(ev) => updateField("Url", ev.target.value)} />
          </label>
          <label>
            Description
            <textarea value={form.Description} onChange={(ev) => updateField("Description", ev.target.value)} rows={3} />
          </label>
          <CustomButton type="submit" variant="contained" color="primary" disabled={busy} data-testid="save-event">
            Save event
          </CustomButton>
        </form>
      ) : null}

      <ul className="calendarList" data-testid="calendar-list">
        {events.length === 0 ? <li className="empty">No upcoming events in this calendar.</li> : null}
        {events.map((ev) => (
          <li key={ev.ID ?? ev.id} data-testid="calendar-event">
            <strong>{ev.Title || ev.title}</strong>
            <span className="eventMeta">
              {eventTypeLabel(ev.Scope ?? ev.scope, ev.EventType ?? ev.eventType)} ·{" "}
              {new Date(ev.StartUtc || ev.startUtc).toLocaleString()}
            </span>
            {(ev.CityName || ev.cityName || ev.CountryName || ev.countryName) && (
              <span className="eventPlace">
                {[ev.CityName || ev.cityName, ev.RegionOrState || ev.regionOrState, ev.CountryName || ev.countryName]
                  .filter(Boolean)
                  .join(", ")}
              </span>
            )}
            {(ev.Url || ev.url) && (
              <a href={ev.Url || ev.url} target="_blank" rel="noreferrer">
                Details
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
